import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { UserDto } from '@picflow/types';
import { setUser } from '@sentry/angular-ivy';
import { CookieService } from 'ngx-cookie-service';
import { ScreenSizes } from '../classes/screen-sizes.class';
import { debugMaxEntries, trialValueMultiplier } from '../consts';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { getPageType } from '../lib/get-page-type';
import { isChecklistDone } from '../lib/is-checklist-done';
import { isIframe } from '../lib/is-iframe';
import { loadScript } from '../lib/load-script';
import { randomString } from '../lib/random-string';
import { throttle } from '../lib/throttle';
import { AppConfigService } from '../providers/config.service';
import { Product, ProductPrice } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { BrowserService } from './browser.service';
import { FeatureFlagsService } from './feature-flags.service';
import { MetaService } from './meta.service';
import { PriceDetails } from './pricing.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any;
    dataLayer: { [key: string]: unknown }[];
    Intercom: (action: string, ...args: unknown[]) => void;
    intercomSettings: { [key: string]: unknown };
    attachEvent: (event: string, callback: () => void) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  initted = false;
  sendLoginEvent = false;
  lastUrl = '';
  cannyIdentified = false;
  lastButtonClicked: { ctaName: string; ctaText: string; ctaIcon: string; ctaLocation: string };
  googleParams: { gclid?: string; gbraid?: string; wbraid?: string } = {};
  lastValues = {};
  isIframe: boolean;

  constructor(
    private router: Router,
    private config: AppConfigService,
    private state: StateService,
    private metaService: MetaService,
    private featureFlags: FeatureFlagsService,
    private route: ActivatedRoute,
    private cookies: CookieService,
    private storage: StorageService,
    private browserService: BrowserService,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    if (isPlatformServer(this.platformId)) return;
    this.isIframe = isIframe();

    this.zone.runOutsideAngular(() => {
      if (this.browserService.isExtension()) {
        window.dataLayer = window.dataLayer || [];
        return;
      }
      this.tagmanagerInit();
      this.segmentInit();
      this.segmentLoad();
    });

    this.router.events.subscribe(async event => {
      if (event instanceof NavigationStart) {
        this.extractAndSaveUtmParams(event.url);
        this.extractAndSaveTrackingParams(event.url);
        this.state.nextPageIsNotFound = false;
      }
      if (event instanceof NavigationEnd) {
        if (this.lastUrl.split('?')[0].split('#')[0] === event.urlAfterRedirects.split('?')[0].split('#')[0]) return;

        this.lastUrl = event.urlAfterRedirects.split('email=')[0];
        const path = event.urlAfterRedirects.split('email=')[0];
        const title = await this.metaService.getTitle(path);
        console.debug('PAGEVIEW:', title);

        const data = getAllRouteData(this.route.snapshot);
        let gallery_id: string;
        let image_id: string;
        let tenant_id: string;
        if (data.gallery) {
          gallery_id = data.gallery.id;
          if (data.gallery.tenant) tenant_id = data.gallery.tenant?.id || data.gallery.tenant;
        }
        if (data.image) image_id = data.image.id;
        if (data.data?.image) image_id = data.data.image.id;

        window.Intercom?.('boot');
        const properties = this.mapTagmanagerData(this.getUserTraits());
        this.pushToDataLayer(properties, true);
        this.pushToDataLayer({
          event: 'page_view',
          page_location: location.origin + path,
          page_title: title,
          gallery_id,
          image_id,
          tenant_id,
        });

        this.segmentPage(
          getPageType(path, {
            isTenantDomain: this.config.isTenantDomain,
          }),
          title,
          path
        );
        this.checkIntercom();

        if (this.state.recordDebug) {
          if (this.state.debugResponseCache.length > debugMaxEntries) this.state.debugResponseCache.shift();
          this.state.debugResponseCache.push({
            page_location: location.origin + path,
            __time: Date.now(),
            __type: 'page',
          });
        }
      }
    });
  }

  getUserType() {
    if (this.state.user) return 'customer';
    const path = getFullPath(this.route.snapshot);
    if (path[0] === 'gallery' || this.config.isTenantDomain) return 'gallery_guest';
    return 'potential_customer';
  }

  identify() {
    const user = this.state.user;
    setUser({ id: user.id });

    const tenant = this.state.activeTenant;

    if (!isPlatformBrowser(this.platformId)) return;
    window.intercomSettings = {
      app_id: this.config.intercomAppId,
      hide_default_launcher: !this.shouldShowIntercomBubble(),
      user_id: user.id,
      user_hash: user.intercom_hash,
      email: user.email,
      featureFlags: this.featureFlags.state().toString(),
    };

    if (tenant) {
      window.intercomSettings.checklist = this.getCheckListStatus();
      window.intercomSettings.instagram = tenant.socials.instagram;
    }

    const values = this.getUtmItentify(user);
    if (values.first_utm) window.intercomSettings.first_utm = values.first_utm;
    if (values.last_utm) window.intercomSettings.last_utm = values.last_utm;
    if (values.first_referrer) window.intercomSettings.first_referrer = values.first_referrer;

    window.Intercom?.('update');
    this.intercomInit();

    this.throttledIdentify();

    if (this.sendLoginEvent) this.track('login'); // track login event after setting data layer variables
    this.sendLoginEvent = false;
  }

  private throttledIdentify = throttle(() => {
    if (this.browserService.isExtension()) return;
    this.cannyIdentify();
    try {
      window.analytics?.identify(this.state.user.id, this.getUserTraits(), {
        integrations: this.getSegmentIntegrations(),
      });
    } catch {
      console.warn('Unable to call segment identify');
    }
  }, 2000);

  private mapTagmanagerData(_data: { [key: string]: string }) {
    const data = Object.assign({}, _data);

    // remove tracking parameters
    delete data.gclid;
    delete data.gbraid;
    delete data.wbraid;
    delete data.ttclid;
    delete data.msclkid;
    delete data.twclid;
    delete data.fbclid;
    delete data.fbc;
    delete data.fbp;

    // clarify button fields
    data.button_name = data.name;
    data.button_location = data.location;
    delete data.name;
    delete data.location;

    return data;
  }

  private getUserTraits() {
    const user = this.state.user;
    const utm = this.getUtmItentify(user);
    const trackingParams = this.getTrackingParams();

    const params = {
      utm_first_source: utm.first_utm ? utm.source : undefined,
      utm_first_medium: utm.first_utm ? utm.medium : undefined,
      utm_first_campaign: utm.first_utm ? utm.campaign : undefined,
      utm_first_term: utm.first_utm ? utm.term : undefined,
      utm_first_content: utm.first_utm ? utm.content : undefined,
      first_referrer: utm.first_referrer ?? undefined,
      first_path: utm.first_path ?? undefined,
      userType: this.getUserType(),
      ...trackingParams,
    };

    if (!user) return params;
    return {
      ...params,
      userId: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
    };
  }

  setLastButtonClicked(ctaName: string, ctaText: string, ctaIcon: string, ctaLocation: string) {
    this.lastButtonClicked = { ctaName, ctaText, ctaIcon, ctaLocation };
  }

  track(
    event: string,
    isImportant = true,
    extraProperties: {
      [key: string]:
        | string
        | number
        | boolean
        | { [key: string]: string | number | boolean }
        | { [key: string]: string | number | boolean }[];
    } = {}
  ) {
    if (!event || typeof event !== 'string') {
      console.log('Track event is not a string', event);
      return;
    }
    console.debug('EVENT:', event, extraProperties);
    if (!isPlatformBrowser(this.platformId)) return;

    if (isImportant) {
      window.Intercom?.('trackEvent', event);
    }
    const order_id = extraProperties.order_id || randomString(8);
    const tenantId = this.state.visitingTenantId ?? this.state.activeTenant?.id;

    const properties = { ...this.getUserTraits(), ...extraProperties, order_id, tenantId };
    this.segmentTrack(event, properties);
    this.pushToDataLayer(this.mapTagmanagerData(properties), true);
    this.pushToDataLayer({ event });

    const eventMap = {
      'checklist item done: uploadAssets': ['First Upload', 'onboarding step done: first upload'],
      'onboarding level: pro': ['Professional'],
    };

    if (eventMap[event]) {
      eventMap[event].forEach((_event: string) => this.track(_event, false, extraProperties));
    }

    if (this.state.recordDebug) {
      if (this.state.debugResponseCache.length > debugMaxEntries) this.state.debugResponseCache.shift();
      this.state.debugResponseCache.push({ ...extraProperties, event, __time: Date.now(), __type: 'track' });
    }
  }

  async segmentTrack(event: string, params: { [key: string]: string | number }) {
    try {
      window.analytics?.track(event, params, {
        integrations: this.getSegmentIntegrations(),
      });
    } catch {
      console.warn('Unable to send segment track');
    }
  }

  async segmentPage(pageType: string, title: string, path: string) {
    try {
      const url = window.location.origin + path;
      window.analytics?.page(
        pageType,
        {
          name: pageType,
          title,
          url,
          path,
          ...this.getUserTraits(),
          ...(this.lastButtonClicked ?? {}),
        },
        {
          integrations: this.getSegmentIntegrations(path),
        }
      );
    } catch {
      console.warn('Unable to send segment page');
    }
  }

  private getEcommerceProperties(
    product: Product,
    price: ProductPrice,
    promo: Promotion,
    priceDetails: PriceDetails,
    multiplier = 1
  ) {
    let value = priceDetails.total;
    if (price.billingFrequency === 'monthly') value *= 9; // assuming 9 months average lifetime
    else if (price.billingFrequency === 'yearly') value *= 1.5; // assuming 1.5 years average lifetime

    return {
      currency: 'USD',
      total: value,
      value: value,
      predicted_ltv: value * multiplier,
      coupon: promo?.code,
      products: [
        {
          product_id: product.id,
          name: product.name,
          price: value * multiplier,
        },
      ],
    };
  }

  startTrialEvent(product: Product, price: ProductPrice, promo: Promotion, priceDetails: PriceDetails) {
    this.track('trial_started', true, {
      order_id: this.state.activeTenant.id + '_trial_started',
      ...this.getEcommerceProperties(product, price, promo, priceDetails, trialValueMultiplier),
    });
  }

  cancelTrialEvent() {
    this.track('trial_cancelled', true, {
      order_id: this.state.activeTenant.id + '_trial_started', // should be trial_started because it will be used to cancel the original conversion
    });
  }

  subscribeEvent(product: Product, price: ProductPrice, promo: Promotion, priceDetails: PriceDetails) {
    const ecommerce = this.getEcommerceProperties(product, price, promo, priceDetails);
    this.track('subscribed', true, ecommerce);
    // Duplicate for facebook through segment.
    this.track('Subscribe', true, {
      ...ecommerce,
      order_id: this.state.activeTenant.id + '_subscribe',
    });
  }

  unsubscribeEvent() {
    this.track('subscribed_cancelled', true, {
      order_id: this.state.activeTenant.id + '_subscribe', // should be subscribe because it will be used to cancel the original conversion
    });
  }

  public changePlanEvent(product: Product, price: ProductPrice, promo: Promotion, priceDetails: PriceDetails) {
    this.track('change_plan_success', true, {
      ...this.getEcommerceProperties(product, price, promo, priceDetails),
    });
  }

  public reportKeyboardShortcut(name: string, location: string, extra: { [key: string]: string | number } = {}) {
    this.track('keyboard_shortcut', false, { name, location, ...extra });
  }

  private shouldShowIntercomBubble() {
    if (this.browserService.isExtension()) return false;
    const path = getFullPath(this.route.snapshot);
    if (!['a', 'auth', 'pricing', 'convert'].includes(path[0])) return false;
    if (['gallery'].includes(path[1])) return false;
    if (
      ['galleries', 'settings', 'onboarding', 'activity'].includes(path[1]) &&
      ScreenSizes.screenSize() === 'mobile'
    ) {
      return false;
    }

    if (!this.state.activeTenant) return false;
    return true;
  }

  private checkIntercom() {
    const shouldHide = !this.shouldShowIntercomBubble();
    if (window.intercomSettings) {
      if (window.intercomSettings.hide_default_launcher !== shouldHide) {
        window.intercomSettings.hide_default_launcher = shouldHide;
        window.Intercom?.('update');
      }
    }

    if (!shouldHide) this.intercomInit();
  }

  public intercomInit() {
    if (window.Intercom) return console.debug('Not loading intercom was already loaded');
    if (this.config.env === 'local' && this.config.origin !== 'http://127.0.0.1:4200')
      return console.debug('Not loading intercom on local as it doesnt work anyway');
    if (this.isIframe) return;

    if (!window.intercomSettings) {
      window.intercomSettings = {
        app_id: this.config.intercomAppId,
        hide_default_launcher: !this.shouldShowIntercomBubble(),
      };
    }

    const i = function () {
      // eslint-disable-next-line prefer-rest-params
      i['c'](arguments);
    };
    i['q'] = [];
    i['c'] = function (args) {
      i['q'].push(args);
    };
    window.Intercom = i;

    if (this.browserService.isExtension()) return;
    loadScript('https://widget.intercom.io/widget/' + this.config.intercomAppId, { ignoreErrors: true });
  }

  private async tagmanagerInit() {
    window.dataLayer = [];
    // Note: Putting this to less than 1.5s will cause lighthouse to count this as extra time to interactive
    // await sleep(thirdPartyScriptDelay);

    if (this.config.env !== 'production') return;
    if (this.browserService.isTenantDomain()) return; // Don't load tagmanager on tenant domains
    if (this.isIframe) return;

    const config = this.config;

    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0],
        j = <HTMLScriptElement>d.createElement(s),
        dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.defer = true;
      j.src = 'https://measure.picflow.com/gtm.js?id=' + i + dl + config.gtmEnvironmentExtra;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', config.gtmWorkspaceId);
  }

  private cannyIdentify() {
    if (this.cannyIdentified) return;
    if (this.isIframe) return;
    this.cannyIdentified = true;
    (function (w, d, i, s) {
      function l() {
        if (!d.getElementById(i)) {
          const f = d.getElementsByTagName(s)[0],
            e = d.createElement(s) as HTMLScriptElement;
          (e.type = 'text/javascript'),
            (e.async = !0),
            (e.src = 'https://canny.io/sdk.js'),
            f.parentNode.insertBefore(e, f);
        }
      }
      if ('function' != typeof w['Canny']) {
        const c = function () {
          // eslint-disable-next-line prefer-rest-params
          c.q.push(arguments);
        };
        (c.q = []),
          (w['Canny'] = c),
          'complete' === d.readyState
            ? l()
            : w.attachEvent
            ? w.attachEvent('onload', l)
            : w.addEventListener('load', l, !1);
      }
    })(window, document, 'canny-jssdk', 'script');

    let amount = 0;
    if (this.state.plan) {
      amount = this.state.plan.price.amount;
      if (this.state.plan.price.billingFrequency === 'yearly') amount /= 12;
    }

    // Use the Canny SDK to identify the current user of your website
    window['Canny']('identify', {
      appID: '611b5553d6c0026f4725118a',
      user: {
        email: this.state.user.email,
        name: this.state.user.first_name + ' ' + this.state.user.last_name,
        id: this.state.user.id,
        companies: this.state.activeTenant
          ? [
              {
                created: new Date(this.state.user.created_at * 1000).toISOString(),
                monthlySpend: amount,
                name: this.state.activeTenant.name,
                id: this.state.activeTenant.id,
              },
            ]
          : [],
        avatarURL: this.state.user.avatar_url,
        created: new Date(this.state.user.created_at * 1000).toISOString(),
      },
    });
  }

  extractAndSaveUtmParams(url: string) {
    if (!this.storage.has('firstReferrer') && document.referrer) {
      this.storage.set('firstReferrer', { time: Date.now(), referrer: document.referrer });
    }
    if (!this.storage.has('firstPath')) {
      this.storage.set('firstPath', { time: Date.now(), url });
    }
    const searchString = url.split('?')[1];
    if (!searchString || !searchString.includes('utm_')) return;
    const utmSearchTerms: { key: string; value: string | number }[] = searchString
      .split('&')
      .filter(s => s.startsWith('utm_'))
      .map(s => ({ key: s.split('=')[0].split('utm_')[1], value: decodeURIComponent(s.split('=')[1]) }))
      .filter(s => ['source', 'medium', 'campaign', 'id', 'term', 'content'].includes(s.key));

    if (!utmSearchTerms.length) return;

    utmSearchTerms.push({ key: 'time', value: Date.now() });

    this.storage.set('lastUtm', utmSearchTerms);
    if (!this.storage.has('firstUtm')) this.storage.set('firstUtm', utmSearchTerms);
  }

  getQueryParam(key: string, url: string) {
    const searchString = url.split('?')[1]?.split('#')[0];
    return searchString
      ?.split('&')
      .find(s => s.startsWith(key))
      ?.split('=')[1];
  }

  private extractAndSaveTrackingParams(url: string) {
    const trackingParams = {
      gclid: this.getQueryParam('gclid', url), // google
      gbraid: this.getQueryParam('gbraid', url), // google
      wbraid: this.getQueryParam('wbraid', url), // google
      ttclid: this.getQueryParam('ttclid', url), // tiktok
      msclkid: this.getQueryParam('msclkid', url), // bing
      twclid: this.getQueryParam('twclid', url), // twitter
      fbclid: this.getQueryParam('fbclid', url), // facebook
      fbc: this.cookies.get('_fbc'), // facebook
      fbp: this.cookies.get('_fbp') || undefined, // facebook
    };
    if (Object.values(trackingParams).some(Boolean)) {
      const params = this.getTrackingParams();
      for (const key in trackingParams) {
        if (trackingParams[key]) params[key] = trackingParams[key];
      }
      this.storage.set('trackingParams', params);
    }
  }

  public getTrackingParams() {
    // googleClickParams is for backwards compatibility
    const params = this.storage.get('trackingParams') || this.storage.get('googleClickParams') || {};

    // Only one of these can be set at a time
    if (params.gclid) {
      delete params.gbraid;
      delete params.wbraid;
    } else if (params.gbraid) {
      delete params.gclid;
      delete params.wbraid;
    } else if (params.wbraid) {
      delete params.gclid;
      delete params.gbraid;
    }

    return params;
  }

  private getUtmItentify(user?: UserDto) {
    const values: { [key: string]: string | number } = {};
    const firstUtm: { key: string; value: string | number }[] = this.storage.get('firstUtm') || [];
    if (firstUtm.length) {
      firstUtm.forEach(data => (values[data.key] = data.value));

      const firstUtmTime = <number>firstUtm.find(t => t.key === 'time').value;

      // Only report first utm when the utm time was before the user created date
      if (!user || firstUtmTime < user.created_at * 1000) {
        firstUtm.forEach(s => s.key === 'time' && (s.value = new Date(s.value).toUTCString()));
        values.first_utm = firstUtm.map(s => `${s.key} = ${s.value}`).join(' | ');
      }
    }
    const firstReferrer = this.storage.get('firstReferrer');
    if (firstReferrer) {
      if (!user || firstReferrer.time < user.created_at * 1000) {
        values.first_referrer = firstReferrer.referrer;
      }
    }

    const firstPath = this.storage.get('firstPath');
    if (firstPath) {
      if (!user || firstPath.time < user.created_at * 1000) {
        values.first_path = firstPath.url;
      }
    }

    const lastUtm: { key: string; value: string | number }[] = this.storage.get('lastUtm') || [];
    if (lastUtm.length) {
      const lastUtmTime = <number>lastUtm.find(t => t.key === 'time').value;

      // Only report last utm when the utm time was before the user created date
      if (!user || lastUtmTime < user.created_at * 1000) {
        lastUtm.forEach(s => s.key === 'time' && (s.value = new Date(s.value).toUTCString()));
        values.last_utm = lastUtm.map(s => `${s.key} = ${s.value}`).join(' | ');
      }
    }
    return values;
  }

  private segmentInit() {
    // Waiting a bit after page load to let the page contents load before loading all the tracking scripts
    // Note: Putting this to less than 1.5s will cause lighthouse to count this as extra time to interactive
    window.analytics = [];
    window.analytics.methods = [
      // 'trackSubmit',
      // 'trackClick',
      // 'trackLink',
      // 'trackForm',
      'pageview',
      'identify',
      // 'reset',
      // 'group',
      'track',
      // 'ready',
      // 'alias',
      // 'debug',
      'page',
      // 'once',
      // 'off',
      // 'on',
      // 'addSourceMiddleware',
      // 'addIntegrationMiddleware',
      // 'setAnonymousId',
      // 'addDestinationMiddleware',
    ];
    window.analytics.factory = function (method) {
      return function () {
        // eslint-disable-next-line prefer-rest-params
        const args = Array.prototype.slice.call(arguments);
        args.unshift(method);
        window.analytics.push(args);
        return window.analytics;
      };
    };
    // For each of our methods, generate a queueing stub.
    for (let i = 0; i < window.analytics.methods.length; i++) {
      const key = window.analytics.methods[i];
      window.analytics[key] = window.analytics.factory(key);
    }
    window.analytics.load = (key, e) => {
      loadScript('https://analytics.picflow.com/analytics.js/v1/' + key + '/analytics.min.js', {
        ignoreErrors: true,
      });
      window.analytics._loadOptions = e;
    };
    window.analytics._writeKey = this.config.segmentKey;
    window.analytics.SNIPPET_VERSION = '4.15.3';
  }

  private async segmentLoad() {
    // await sleep(thirdPartyScriptDelay);
    // if (this.isIframe) return; https://app.asana.com/0/1200658527198468/1206010622378261/f
    window.analytics.load?.(this.config.segmentKey); // load is not defined in unit tests
  }

  private getCheckListStatus() {
    const map = {
      uploadAssets: 'Upload',
      setCover: 'Cover',
      uploadLogo: 'Logo',
      firstFeedback: 'Review',
      deliverAssets: 'Deliver',
      visitDash: 'Dash',
    };
    const checkList = this.state.activeTenant.onboarding_checklist;
    const done = isChecklistDone(this.state.activeTenant);
    if (done) return '✅';
    if (!checkList) return '❌';
    return (
      Object.keys(checkList)
        .map(key => `${checkList[key] ? '✅' : '❌'} ${map[key]}`)
        .join(' ') || '❌'
    );
  }

  private pushToDataLayer(obj: { [key: string]: string | number }, ignoreDuplicates = false) {
    if (ignoreDuplicates) {
      const sameKeys = Object.keys(obj).filter(key => this.lastValues[key] === obj[key]);
      sameKeys.forEach(key => delete obj[key]);
      if (Object.keys(obj).length === 0) return;
      this.lastValues = { ...this.lastValues, ...obj };
    }

    window.dataLayer.push(obj);
  }

  private getSegmentIntegrations(path?: string) {
    const userType = this.getUserType();
    const isAssetPage = path && this.browserService.urlIsAssetUrl(path);
    if (userType === 'customer') return { All: true, 'Mixpanel (Actions)': !isAssetPage };
    return { All: true, 'Mixpanel (Actions)': !isAssetPage };
  }
}
